/**
 * "More" overflow for pipeline stage tabs: stages that do not fit the toolbar
 * keep their label, count, and navigation behavior here. The trigger reuses
 * the inactive SegmentedButton segment look so it reads as part of the tabs.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu } from '@/components/ui/organisms/DropdownMenu';
import { StatusTabCount } from './StatusTabCount';
import type { StatusTabItem } from './StatusTabs';
import { STATUS_TABS_ICON_CLASS, STATUS_TABS_MORE_CLASS } from './statusTabs.config';

export interface StatusTabsMoreProps {
  tabs: StatusTabItem[];
  activeId: string;
  onSelect: (stageId: string) => void;
  /** GTM attributes per stage id, identical to the visible tabs. */
  trackingById?: Record<string, Record<string, string>>;
}

export const StatusTabsMore: React.FC<StatusTabsMoreProps> = ({
  tabs,
  activeId,
  onSelect,
  trackingById,
}) => {
  const { t } = useTranslation('job');

  return (
    <DropdownMenu className="shrink-0">
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={STATUS_TABS_MORE_CLASS}
          aria-label={t('status_tabs.more_aria', { count: tabs.length })}
        >
          <span>{t('status_tabs.more')}</span>
          <span className={STATUS_TABS_ICON_CLASS} aria-hidden>
            <ChevronDown />
          </span>
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start">
        {tabs.map((tab) => {
          const isActive = tab.id === activeId;
          return (
            <DropdownMenu.Item
              key={tab.id}
              aria-current={isActive ? 'true' : undefined}
              {...trackingById?.[tab.id]}
              onClick={() => onSelect(tab.id)}
            >
              <span className="flex-1 text-left">{tab.label}</span>
              <StatusTabCount count={tab.count} isActive={isActive} />
            </DropdownMenu.Item>
          );
        })}
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};
